import type { IntlShape } from 'react-intl'
import { DEFAULT_REVIEWS_TOTAL_THRESHOLD, RECOMMENDED_RATING_THRESHOLD } from 'constants/review'
import { isOrderPast } from 'lib/order/isUpcoming'
import { getReviewableStays } from './reviewableStays'

interface ReviewOrderImage {
  image?: App.Image
  imageId?: string
}

interface ReviewOrderDisplayDetails {
  endDate?: string
  startDate?: string
  title?: string
}

interface ReviewOrderDisplayDetailsParams {
  accommodationItem?: App.OrderItem
  bedbankItem?: App.OrderBedbankItem
  experience?: App.ExperienceOffer
  experienceItem?: App.OrderExperienceItem
  hotelType?: App.OfferType
  orderType?: 'hotel' | 'experience' | 'tour' | 'bedbank'
  tour?: App.Tours.TourV2OfferSnapshot
  tourItem?: App.Tours.TourV2OrderItem
}

export type ReviewOrderType = 'stay' | 'experience' | 'tour'

export function getReviewOrderType(order: App.Order): ReviewOrderType | undefined {
  if (order.tourItems.length) return 'tour'
  if (order.items.length || order.bedbankItems.length) return 'stay'
  if (order.experienceItems.length) return 'experience'
  return undefined
}

export function getReviewOrderImage(order: App.Order, tourImageId?: string, experience?: App.ExperienceOffer): ReviewOrderImage {
  const orderType = getReviewOrderType(order)

  if (orderType === 'experience') return { image: experience?.images[0] }
  if (orderType === 'tour') return { imageId: tourImageId }
  if (order.bedbankItems.length) return { imageId: order.bedbankItems[0]?.offer.image.id }
  if (order.items.length) return { imageId: order.items[0]?.offer.image.id }
  return {}
}

export function getReviewOrderDisplayDetails(order: App.Order, tour?: App.Tours.TourV2OfferSnapshot, experience?: App.ExperienceOffer): ReviewOrderDisplayDetails {
  const reviewOrderType = getReviewOrderType(order)
  const orderType = reviewOrderType === 'stay' ? 'hotel' : reviewOrderType

  return getReviewOrderDisplayDetailsFromItems({
    accommodationItem: order.items[0],
    bedbankItem: order.bedbankItems[0],
    experience,
    experienceItem: order.experienceItems[0],
    hotelType: order.bedbankItems.length ? 'bedbank_hotel' : undefined,
    orderType,
    tour,
    tourItem: order.tourItems[0],
  })
}

export function getReviewOrderDisplayDetailsFromItems(params: ReviewOrderDisplayDetailsParams): ReviewOrderDisplayDetails {
  const {
    accommodationItem,
    bedbankItem,
    experience,
    experienceItem,
    hotelType,
    orderType,
    tour,
    tourItem,
  } = params

  if (orderType === 'experience') {
    return {
      title: experience?.name ?? experienceItem?.title,
      startDate: experienceItem?.date,
      endDate: experienceItem?.date,
    }
  }

  if (orderType === 'tour') {
    return {
      title: tour?.tourName ?? tourItem?.tour.title,
      startDate: tour?.startDate ?? tourItem?.tour.startDate,
      endDate: tour?.endDate ?? tourItem?.tour.endDate,
    }
  }

  if (hotelType === 'bedbank_hotel' || orderType === 'bedbank' || bedbankItem) {
    return {
      title: bedbankItem?.offer.name,
      startDate: bedbankItem?.checkIn?.toISOString(),
      endDate: bedbankItem?.checkOut?.toISOString(),
    }
  }

  if (accommodationItem) {
    return {
      title: accommodationItem.offer.property?.name ?? accommodationItem.offer.name,
      startDate: accommodationItem.reservation?.startDate,
      endDate: accommodationItem.reservation?.endDate,
    }
  }

  return {}
}

export function checkOrderAcceptsReview(order?: App.Order) {
  if (!order) return false

  const orderType = getReviewOrderType(order)
  if (orderType === 'stay') {
    return getReviewableStays(order).length > 0
  }

  const isTravelledOrder = isOrderPast(order)
  const isValidOrderType = !!(order.tourItems.length || order.experienceItems.length)
  const isCompletedItem = isOrderItemCompleted(order)

  return isTravelledOrder && isValidOrderType && isCompletedItem
}

/**
 * For now we're only getting the first item inside order to leave reviews
 * So we get only 1 review by order. For this reason, we check status of the first item
 */
export function isOrderItemCompleted(order: App.Order): boolean {
  const orderType = getReviewOrderType(order)

  if (orderType === 'tour') return order.tourItems[0].status === 'completed'
  if (orderType === 'experience') return order.experienceItems[0].status === 'completed'
  if (order.bedbankItems.length) return order.bedbankItems[0].status === 'completed'
  return !!order.items.length && order.items[0].status === 'completed'
}

export function isOfferRatingDisplayable(
  rating: App.OfferRating | undefined,
  /**
   * Minimum number of reviews required on a rating to be displayed.
   */
  reviewsTotalThreshold: number = DEFAULT_REVIEWS_TOTAL_THRESHOLD,
): rating is App.OfferRating {
  if (!rating) return false
  return !!rating.score && rating.reviewsTotal >= reviewsTotalThreshold
}

export function isExperienceOfferRatingDisplayable(experienceOffer: App.ExperienceOffer | App.ExperienceBookingView): boolean {
  return !!experienceOffer.rating?.score && !experienceOffer.disableReviews
}

export const getRecommendsLabel = (review: App.Review, intl: IntlShape) => {
  const isRecommended = review.rating >= RECOMMENDED_RATING_THRESHOLD

  if (review.experienceId) {
    return isRecommended ? intl.formatMessage({
      id: 'xMalltHs-1j8CohvT8Br',
      defaultMessage: 'Recommends this experience',
      description: 'Review label indicating the reviewer recommends this experience to others',
    }) : intl.formatMessage({
      id: 'aXudOaY2TNqfC4NRl-Jm',
      defaultMessage: 'Does not recommend this experience',
      description: 'Review label indicating the reviewer does not recommend this experience',
    })
  }

  if (review.tourId) {
    return isRecommended ? intl.formatMessage({
      id: 'HpnG51sEG_9mNG5-f3gY',
      defaultMessage: 'Recommends this tour',
      description: 'Review label indicating the reviewer recommends this tour to other travellers',
    }) : intl.formatMessage({
      id: 'vB4z0a5uWdwZMQAmUsEc',
      defaultMessage: 'Does not recommend this tour',
      description: 'Review label indicating the reviewer does not recommend this tour to others',
    })
  }

  return isRecommended ? intl.formatMessage({
    id: 'jgHPnGx0HChhd8XLeoN1',
    defaultMessage: 'Recommends this hotel',
    description: 'Review label indicating the reviewer recommends this hotel to other travellers',
  }) : intl.formatMessage({
    id: 'YH4L1gG4uh8j8v_F3wPa',
    defaultMessage: 'Does not recommend this hotel',
    description: 'Review label indicating the reviewer does not recommend this hotel to others',
  })
}
